import { useCallback } from 'react';
import { CallResult, EntityType } from '@weave/schema-gen-ts/dist/schemas/sms/shared/v2/enums.pb';
import { InboxThreadItemAlert_ActionType } from '@weave/schema-gen-ts/dist/shared/notification/alerts.pb';
import { NotificationType } from '@weave/schema-gen-ts/dist/shared/notification/notifications.pb';
import { SMSDataV4, SMSDataSharedUtils } from '@frontend/api-sms-data';
import { InboxLocationPhones } from '@frontend/inbox-location-phones';
import { useAppScopeStore } from '@frontend/scope';
import { GetWebsocketEventHandler, useWebsocketEventSubscription } from '@frontend/websocket';

/**
 * A hook that returns a function to execute when an INBOX_THREAD_ITEM event is received.
 */
export const useInboxThreadItemWSHandler = () => {
  const { upsertThreadItem, removeVoiceThreadItem } = SMSDataV4._QueryUpdaters.useUnifiedThreadUpdater();
  const { isCallRecordItem, getLocationIdFromThreadItem } = SMSDataSharedUtils.threadItemsUtils;
  const getUnifiedThreadEnabled = SMSDataSharedUtils.useGetUnifiedThreadEnabled();

  const { selectedLocationIds } = useAppScopeStore();
  const checkUserLocationPhoneAccess = InboxLocationPhones.Hooks.useCheckUserLocationPhoneAccess(selectedLocationIds);

  const wsEventHandler = useCallback<GetWebsocketEventHandler<NotificationType.INBOX_THREAD_ITEM>>(
    async (event) => {
      const { params } = event;

      // If User does not have access to location phone number in the event's thread, ignore the event
      if (!!params.weaveNumber && !checkUserLocationPhoneAccess(params.weaveNumber)) {
        return;
      }

      const locationId = getLocationIdFromThreadItem(params.item);

      if (isCallRecordItem(params.item) && getUnifiedThreadEnabled([locationId])) {
        switch (params.actionType) {
          case InboxThreadItemAlert_ActionType.ACTION_TYPE_INBOUND:
          case InboxThreadItemAlert_ActionType.ACTION_TYPE_OUTBOUND:
            await upsertThreadItem({
              matchValues: {
                threadId: params.threadId,
                threadItemId: params.item.callRecord.threadCallId,
                locationId,
                entityType:
                  params.item.callRecord.entityType === EntityType.ENTITY_TYPE_ROBOCALL
                    ? EntityType.ENTITY_TYPE_ROBOCALL
                    : params.item.callRecord.callResult === CallResult.CALL_RESULT_VOICEMAIL
                    ? EntityType.ENTITY_TYPE_VOICEMAIL
                    : EntityType.ENTITY_TYPE_CALL,
              },
              updater: () => params.item,
              isOptimistic: false,
            });
            break;
          case InboxThreadItemAlert_ActionType.ACTION_TYPE_DELETE: // filters out the threadItem: only voicemail is supported now.
            if (isCallRecordItem(params.item)) {
              removeVoiceThreadItem({
                threadId: params.threadId,
                threadCallId: params.item.callRecord.threadCallId,
                locationId,
              });
            }
            break;
        }
      }
    },
    [upsertThreadItem, isCallRecordItem, getUnifiedThreadEnabled, removeVoiceThreadItem]
  );

  return wsEventHandler;
};

export const useInboxThreadItemWSSubscription = () => {
  const handler = useInboxThreadItemWSHandler();
  return useWebsocketEventSubscription(NotificationType.INBOX_THREAD_ITEM, handler);
};
